import {Button, ButtonGroup, Toolbar, Tooltip, Typography} from "@project/design-system-web";
import {useI18n} from "@project/i18n/react";
import {CopyIcon, ResetIcon} from "@project/icons-web";
import {useDebouncedValue} from "@tanstack/react-pacer";
import mermaid from "mermaid";
import {useEffect, useRef} from "react";
import svgPanZoom from "svg-pan-zoom";

import {componentMessages} from "../components.messages";

// fallback type since TS is complaining about the Instance export
type SvgPanZoomInstance = ReturnType<typeof svgPanZoom>;

export interface MermaidDiagramProps {
  code: string;
  className?: string;
  /** Controls if the container is fixed height (chat) or flexible (full page) */
  isChatMode?: boolean;
}

export function MermaidDiagram({code, className = "", isChatMode = false}: MermaidDiagramProps) {
  const {t} = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);
  const panZoomRef = useRef<SvgPanZoomInstance | null>(null);

  // Debounce the code during streaming to prevent excessive resizing and flickering
  const [debouncedCode] = useDebouncedValue(code, {
    wait: 400,
  });

  const waitingForValidDiagramLabel = t(componentMessages.mermaidWaitingForValidDiagram);
  const diagramActionsLabel = t(componentMessages.mermaidActions);
  const copyCodeLabel = t(componentMessages.mermaidCopyCode);
  const resetZoomLabel = t(componentMessages.mermaidResetZoom);

  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: "default",
      securityLevel: "loose",
      suppressErrorRendering: true,
    });
  }, []);

  useEffect(() => {
    if (!containerRef.current || !debouncedCode) return;

    let disposed = false;

    const cleanupPanZoom = () => {
      if (panZoomRef.current) {
        panZoomRef.current.destroy();
        panZoomRef.current = null;
      }
    };

    const renderDiagram = async () => {
      try {
        const cleanCode = debouncedCode
          .replace(/^```mermaid\n?/, "")
          .replace(/```$/, "")
          .trim();

        if (!cleanCode) return;

        const id = `mermaid-${Math.random().toString(36).slice(2)}`;

        // mermaid.parse can be used to validate, but checking for valid structure is also good
        const {svg, bindFunctions} = await mermaid.render(id, cleanCode);

        // Mermaid sometimes returns an error SVG instead of throwing
        if (svg.includes("Syntax error in text") || svg.includes("Syntax error")) {
          throw new Error("Mermaid syntax error");
        }

        if (!containerRef.current || disposed) return;

        // Only cleanup panZoom once we have a valid SVG to replace it with
        cleanupPanZoom();

        const parsedSvg = new DOMParser().parseFromString(svg, "image/svg+xml");
        const nextSvg = parsedSvg.documentElement;

        if (nextSvg.tagName.toLowerCase() !== "svg" || parsedSvg.querySelector("parsererror")) {
          throw new Error("Mermaid SVG parse error");
        }

        containerRef.current.replaceChildren(nextSvg);

        // Important: preserve Mermaid interactivity such as click callbacks/tooltips
        bindFunctions?.(containerRef.current);

        const svgEl = containerRef.current.querySelector("svg");

        if (!svgEl) return;

        // Nuke Mermaid's inline constraints
        svgEl.removeAttribute("height");
        svgEl.removeAttribute("width");
        svgEl.removeAttribute("style");

        svgEl.style.width = "100%";
        svgEl.style.height = "100%";
        svgEl.style.display = "block";

        panZoomRef.current = svgPanZoom(svgEl, {
          zoomEnabled: true,
          controlIconsEnabled: false, // we'll make our own buttons
          fit: true,
          center: true,
          minZoom: 0.5,
          maxZoom: 10,
          zoomScaleSensitivity: 0.25,
          dblClickZoomEnabled: true,
          mouseWheelZoomEnabled: true,
          preventMouseEventsDefault: true,
          panEnabled: true,
        });
      } catch {
        // If we are in chat mode (streaming), we ignore parsing errors and keep the last valid render
        // until the stream produces valid syntax again. If not in chat mode, or if the container is empty,
        // we show the error state.
        if (containerRef.current && !disposed) {
          if (!isChatMode || containerRef.current.childElementCount === 0) {
            const fallback = document.createElement("div");

            fallback.className = "p-4 text-center text-danger-600 text-sm";
            fallback.textContent = waitingForValidDiagramLabel;
            containerRef.current.replaceChildren(fallback);
          }
        }
      }
    };

    void renderDiagram();

    return () => {
      disposed = true;
      cleanupPanZoom();
    };
  }, [debouncedCode, isChatMode, waitingForValidDiagramLabel]);

  const reset = () => {
    const instance = panZoomRef.current;

    if (!instance) return;
    instance.resize();
    instance.fit();
    instance.center();
  };

  return (
    <div className={`relative flex flex-col gap-2 ${className}`}>
      <Toolbar aria-label={diagramActionsLabel} className="self-end">
        <ButtonGroup variant="tertiary">
          <Tooltip delay={0}>
            <Button
              aria-label={copyCodeLabel}
              variant="secondary"
              isIconOnly
              onPress={() => void navigator.clipboard.writeText(code)}
            >
              <CopyIcon className="size-4" />
            </Button>
            <Tooltip.Content>
              <Typography>{copyCodeLabel}</Typography>
            </Tooltip.Content>
          </Tooltip>

          <Tooltip delay={0}>
            <Button aria-label={resetZoomLabel} variant="secondary" isIconOnly onPress={reset}>
              <ButtonGroup.Separator />
              <ResetIcon className="size-4" />
            </Button>
            <Tooltip.Content>
              <Typography>{resetZoomLabel}</Typography>
            </Tooltip.Content>
          </Tooltip>
        </ButtonGroup>
      </Toolbar>

      <div
        ref={containerRef}
        className={`w-full overflow-hidden rounded border bg-white ${
          isChatMode ? "h-75" : "flex-1"
        }`}
      />
    </div>
  );
}
